/**
 * @fileoverview Framework-agnostic foundation for the hybrid WYSIWYG document builder:
 * the component palette catalog and pure, immutable band operations on a DocumentLayout.
 * @module document-render/documentBuilderPalette
 *
 * Responsibilities: - Define the drag-drop palette (Structural / Content / Dynamic-data groups).
 *                   - Provide pure add/move/remove/duplicate band operations + dynamic-band
 *                     affordance metadata (so the canvas can clearly mark expanding lists).
 * Dependencies:     - ./documentLayout (Band/BandKind/DocumentLayout model).
 * Architecture:     - Pure (no React, no I/O); the React canvas (ui-admin) renders from this.
 *                     Keeps builder logic testable and shared between tenant-web and system-web.
 * Notes:            - Dynamic bands expand per inspection data at render time (D2 affordance).
 */

import type { Band, BandKind, BindingDescriptor, DocumentLayout, Element } from './documentLayout';

export type PaletteGroup = 'structural' | 'content' | 'dynamic';

/**
 * @contract PaletteItem
 * Purpose: One draggable component the author can place on the canvas.
 * Fields:  kind, label, group, dynamic (expands per data), description, allowMultiple.
 * Rules:   `dynamic` items render via the Phase 1 expansion engine and must show the
 *          "repeats per item" affordance (D2). Some structural bands are singletons.
 * Sync:    N/A (static catalog).
 */
export interface PaletteItem {
  kind: BandKind;
  label: string;
  group: PaletteGroup;
  dynamic: boolean;
  description: string;
  /** false → at most one of this band kind in the body (e.g. letterhead, cover, footer). */
  allowMultiple: boolean;
}

/** The full palette catalog, grouped Structural / Content / Dynamic-data (Phase 2 §C1). */
export const PALETTE_ITEMS: PaletteItem[] = [
  // Structural
  { kind: 'letterhead', label: 'Letterhead', group: 'structural', dynamic: false, allowMultiple: false, description: 'Department logo, name, and contact header.' },
  { kind: 'coverPage', label: 'Cover Page', group: 'structural', dynamic: false, allowMultiple: false, description: 'Leading cover page before the document body.' },
  { kind: 'recipientBlock', label: 'Recipient Block', group: 'structural', dynamic: false, allowMultiple: false, description: 'Owner / responsible-party address block.' },
  { kind: 'subjectLine', label: 'Subject Line', group: 'structural', dynamic: false, allowMultiple: false, description: 'RE: subject line.' },
  { kind: 'salutation', label: 'Salutation', group: 'structural', dynamic: false, allowMultiple: false, description: 'Dear … greeting.' },
  { kind: 'signatureBlock', label: 'Signature Block', group: 'structural', dynamic: false, allowMultiple: true, description: 'Inspector / responsible-party signatures.' },
  { kind: 'legal', label: 'Legal Text', group: 'structural', dynamic: false, allowMultiple: false, description: 'Disclaimers and legal language.' },
  { kind: 'footer', label: 'Footer', group: 'structural', dynamic: false, allowMultiple: false, description: 'Page footer (page numbers, confidentiality).' },
  // Content
  { kind: 'richText', label: 'Rich Text', group: 'content', dynamic: false, allowMultiple: true, description: 'Formatted paragraph with merge fields, color, fonts, tables.' },
  { kind: 'image', label: 'Image / Logo', group: 'content', dynamic: false, allowMultiple: true, description: 'Static image, logo, decal, or seal.' },
  { kind: 'divider', label: 'Divider', group: 'content', dynamic: false, allowMultiple: true, description: 'Horizontal rule.' },
  { kind: 'spacer', label: 'Spacer', group: 'content', dynamic: false, allowMultiple: true, description: 'Vertical spacing.' },
  { kind: 'pageBreak', label: 'Page Break', group: 'content', dynamic: false, allowMultiple: true, description: 'Force the next content onto a new page.' },
  { kind: 'callout', label: 'Callout / Border Box', group: 'content', dynamic: false, allowMultiple: true, description: 'Bordered box for emphasis.' },
  // Dynamic data
  { kind: 'violationsList', label: 'Violations List', group: 'dynamic', dynamic: true, allowMultiple: false, description: 'Repeats per violation; expands with inspection data.' },
  { kind: 'photoGallery', label: 'Photo Gallery', group: 'dynamic', dynamic: true, allowMultiple: false, description: 'Repeats per photo; expands with inspection photos.' },
  { kind: 'metadataGrid', label: 'Metadata Grid', group: 'dynamic', dynamic: true, allowMultiple: false, description: 'Inspection metadata key/value grid.' },
  { kind: 'formResponses', label: 'Form Responses', group: 'dynamic', dynamic: true, allowMultiple: false, description: 'Repeats per form/field; expands with responses.' },
  { kind: 'advancedFields', label: 'Advanced Fields', group: 'dynamic', dynamic: true, allowMultiple: false, description: 'Bound inspection fields; expands per binding.' },
];

const PALETTE_BY_KIND: Record<string, PaletteItem> = Object.fromEntries(
  PALETTE_ITEMS.map((i) => [i.kind, i]),
);

/** Lookup the palette item for a band kind (undefined if unknown). */
export function paletteItemFor(kind: BandKind): PaletteItem | undefined {
  return PALETTE_BY_KIND[kind];
}

/**
 * Band kinds that must never be added inside a cover page. A cover page is a standalone single
 * leading page, so nesting another cover page (recursion), the document footer, or a page break
 * (the cover is one page) is structurally invalid.
 */
export const COVER_SURFACE_EXCLUDED_KINDS: readonly BandKind[] = ['coverPage', 'footer', 'pageBreak'];

/**
 * @process paletteItemsForSurface
 * Steps:   Return the palette items offerable on `surface`. The body surface offers everything;
 *          the cover-page surface filters out COVER_SURFACE_EXCLUDED_KINDS so the "Add component"
 *          panel can never add a cover page (or footer / page break) inside a cover page.
 * Inputs:  surface ('body' | 'coverPage').
 * Outputs: PaletteItem[] (same array identity for body; a filtered copy for cover).
 * Idempotency: REQUIRED (pure).
 */
export function paletteItemsForSurface(surface: 'body' | 'coverPage'): PaletteItem[] {
  if (surface !== 'coverPage') return PALETTE_ITEMS;
  return PALETTE_ITEMS.filter((i) => !COVER_SURFACE_EXCLUDED_KINDS.includes(i.kind));
}

/** True when a band kind expands per inspection data at render time (needs the affordance UX). */
export function isDynamicBand(kind: BandKind): boolean {
  return PALETTE_BY_KIND[kind]?.dynamic === true;
}

/**
 * Affordance label for a dynamic band, e.g. "Violations — repeats per item; expands with data."
 * Returns null for non-dynamic bands.
 */
export function dynamicBandAffordance(kind: BandKind): string | null {
  const item = PALETTE_BY_KIND[kind];
  if (!item || !item.dynamic) return null;
  return `${item.label} — ${item.description}`;
}

/**
 * Maps a dynamic band kind to the inspection data source it expands from at render time.
 * Dynamic bands carry a binding (not authored HTML) so they fill with real inspection data.
 * Downstream mirror: document-ai's documentRecreateService keeps a local copy of this map
 * (to avoid depending on the render tier) — keep both in sync when adding a dynamic kind.
 */
export const DYNAMIC_BAND_BINDING: Partial<Record<BandKind, BindingDescriptor['source']>> = {
  violationsList: 'violations',
  photoGallery: 'photos',
  metadataGrid: 'metadata',
  formResponses: 'formResponses',
  advancedFields: 'advancedField',
};

/** Band kinds that are purely visual separators and never carry authored content. */
const CONTENTLESS_BAND_KINDS: ReadonlySet<BandKind> = new Set<BandKind>(['divider', 'spacer', 'pageBreak']);

/** True when a band kind can hold author-supplied rich-text HTML (not dynamic, not a separator). */
export function canCarryAuthoredHtml(kind: BandKind): boolean {
  return !DYNAMIC_BAND_BINDING[kind] && !CONTENTLESS_BAND_KINDS.has(kind);
}

/**
 * Build the elements for a band of `kind` carrying optional authored `html`.
 * - Dynamic data bands get a binding element (expanded per inspection data at render time).
 * - Separator bands (divider/spacer/pageBreak) get no elements.
 * - Every other kind keeps the authored html as a text element, so structural bands
 *   (recipientBlock, salutation, signatureBlock, legal, letterhead, footer, …) never drop content.
 */
export function buildBandElements(bandId: string, kind: BandKind, html?: string): Element[] {
  const source = DYNAMIC_BAND_BINDING[kind];
  if (source) return [{ id: `${bandId}-el`, type: 'field', binding: { source } }];
  if (CONTENTLESS_BAND_KINDS.has(kind)) return [];
  const safe = typeof html === 'string' ? html : '';
  if (safe) return [{ id: `${bandId}-el`, type: 'text', html: safe }];
  // richText keeps an empty text element so the editor has a content slot to type into.
  return kind === 'richText' ? [{ id: `${bandId}-el`, type: 'text', html: '' }] : [];
}

let bandCounter = 0;
function newBandId(kind: BandKind): string {
  bandCounter += 1;
  return `band-${kind}-${Date.now().toString(36)}-${bandCounter}`;
}

/**
 * @process createBand
 * Steps:   Build a new enabled Band of the given kind via buildBandElements, so the manual
 *          palette-add path produces the same element structure as the AI recreate/agent paths:
 *          dynamic bands carry a binding, separators stay empty, rich-text gets an empty slot.
 * Inputs:  BandKind, optional authored html (structural/content bands).
 * Outputs: Band.
 * Idempotency: NOT REQUIRED (generates a fresh id).
 */
export function createBand(kind: BandKind, html?: string): Band {
  const id = newBandId(kind);
  return { id, kind, enabled: true, elements: buildBandElements(id, kind, html) };
}

/**
 * @process addBand
 * Steps:   Insert a band at `index` (or append). Singleton kinds (allowMultiple=false) are
 *          not added when one already exists — returns the layout unchanged. When `surface` is
 *          'coverPage', COVER_SURFACE_EXCLUDED_KINDS (cover page / footer / page break) are
 *          refused, so the structural constraint is enforced in the shared core rather than only
 *          in the palette UI — every caller (canvas surface op, future programmatic callers)
 *          shares one enforcement point (G-DRY).
 * Inputs:  layout, band, optional index, optional surface ('body' default | 'coverPage').
 * Outputs: new DocumentLayout (immutable).
 * Idempotency: NOT REQUIRED.
 */
export function addBand(
  layout: DocumentLayout,
  band: Band,
  index?: number,
  surface: 'body' | 'coverPage' = 'body',
): DocumentLayout {
  if (surface === 'coverPage' && COVER_SURFACE_EXCLUDED_KINDS.includes(band.kind)) {
    return layout; // structurally invalid on a cover page (no nested cover / footer / page break)
  }
  const item = PALETTE_BY_KIND[band.kind];
  if (item && !item.allowMultiple && layout.body.some((b) => b.kind === band.kind)) {
    return layout; // singleton already present
  }
  const body = [...layout.body];
  const at = index === undefined || index < 0 || index > body.length ? body.length : index;
  body.splice(at, 0, band);
  return { ...layout, body };
}

/**
 * @process moveBand
 * Steps:   Move the band with `id` to `toIndex` (clamped). No-op if id not found.
 * Idempotency: NOT REQUIRED.
 */
export function moveBand(layout: DocumentLayout, id: string, toIndex: number): DocumentLayout {
  const from = layout.body.findIndex((b) => b.id === id);
  if (from === -1) return layout;
  const body = [...layout.body];
  const [moved] = body.splice(from, 1);
  const clamped = Math.max(0, Math.min(toIndex, body.length));
  body.splice(clamped, 0, moved);
  return { ...layout, body };
}

/** Remove the band with `id` (no-op if absent). Immutable. */
export function removeBand(layout: DocumentLayout, id: string): DocumentLayout {
  if (!layout.body.some((b) => b.id === id)) return layout;
  return { ...layout, body: layout.body.filter((b) => b.id !== id) };
}

/**
 * @process duplicateBand
 * Steps:   Clone the band with `id` (fresh ids) and insert directly after it. Singleton kinds
 *          are not duplicated. No-op if id absent.
 * Idempotency: NOT REQUIRED.
 */
export function duplicateBand(layout: DocumentLayout, id: string): DocumentLayout {
  const idx = layout.body.findIndex((b) => b.id === id);
  if (idx === -1) return layout;
  const src = layout.body[idx];
  const item = PALETTE_BY_KIND[src.kind];
  if (item && !item.allowMultiple) return layout;
  const clone: Band = {
    ...src,
    id: newBandId(src.kind),
    elements: src.elements.map((el) => ({ ...el, id: `${newBandId(src.kind)}-el` })),
  };
  const body = [...layout.body];
  body.splice(idx + 1, 0, clone);
  return { ...layout, body };
}

// ------------------------------------------------------------
// Single-canvas activation list (shared by tenant-web + system-web)
// ------------------------------------------------------------

/**
 * @contract BandActivationEntry
 * Purpose: One row in the left activation checklist that drives the single canvas.
 * Fields:  id (band id), label (palette display name), enabled, kind, slot.
 * Rules:   `enabled` is `band.enabled !== false` (bands default to enabled).
 * Sync:    derived from the in-memory DocumentLayout; not persisted separately.
 */
export interface BandActivationEntry {
  id: string;
  label: string;
  enabled: boolean;
  kind: BandKind;
  slot: 'header' | 'body' | 'footer';
}

/**
 * @process bandActivationList
 * Steps:   Flatten header + body[] + footer of a layout into activation rows (in render order).
 * Inputs:  the current DocumentLayout.
 * Outputs: BandActivationEntry[] used by the left checklist.
 * Failure Handling: none — pure projection.
 * Idempotency: REQUIRED — pure function of its input.
 */
export function bandActivationList(layout: DocumentLayout): BandActivationEntry[] {
  const row = (b: Band, slot: BandActivationEntry['slot']): BandActivationEntry => ({
    id: b.id,
    label: paletteItemFor(b.kind)?.label ?? b.kind,
    enabled: b.enabled !== false,
    kind: b.kind,
    slot,
  });
  return [
    ...(layout.header ? [row(layout.header, 'header')] : []),
    ...layout.body.map((b) => row(b, 'body')),
    ...(layout.footer ? [row(layout.footer, 'footer')] : []),
  ];
}

/**
 * @process setBandEnabled
 * Steps:   Immutably flip a band's `enabled` across header/body/footer by id.
 * Inputs:  layout, band id, enabled flag.
 * Outputs: a new DocumentLayout with that band toggled (other bands untouched).
 * Failure Handling: unknown id → layout returned structurally unchanged.
 * Idempotency: REQUIRED — pure function of its inputs.
 */
export function setBandEnabled(layout: DocumentLayout, id: string, enabled: boolean): DocumentLayout {
  const patch = (b: Band): Band => (b.id === id ? { ...b, enabled } : b);
  return {
    ...layout,
    header: layout.header ? patch(layout.header) : layout.header,
    body: layout.body.map(patch),
    footer: layout.footer ? patch(layout.footer) : layout.footer,
  };
}
